import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { CreditBalance } from '@/components/wizard/CreditDisplay';
import { StepProgressIndicator } from '@/components/onboarding/OnboardingHelpers';
import { 
  ArrowRight,
  Check,
  Rocket,
  Lock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { WIZARD_STEPS } from '@/lib/constants';
import { getStrategyById, getVariantById } from '@/config/funnelStrategies';
import { useSwipe } from '@/hooks/useSwipe';

// Import wizard steps
import StrategyStep from './wizard/StrategyStep';
import BriefStep from './wizard/BriefStep';
import LandingsStep from './wizard/LandingsStep';
import SequencesStep from './wizard/SequencesStep';
import TrafficStep from './wizard/TrafficStep';
import CampaignsStep from './wizard/CampaignsStep';
import IntegrationStep from './wizard/IntegrationStep';
import FunnelDashboard from './wizard/FunnelDashboard';

// Step approval requirements with blocking/warning behavior
const STEP_REQUIREMENTS = {
  1: { 
    items: ['strategy', 'variant'], 
    labels: { 
      strategy: 'Estrategia', 
      variant: 'Variante de estrategia'
    },
    blocking: true
  },
  2: { 
    items: ['client', 'funnelName', 'brief_basics'],
    labels: { 
      client: 'Cliente asignado',
      funnelName: 'Nombre del funnel',
      brief_product_expert: 'Nombre del producto y Nombre del experto (en el brief)',
      brief_product: 'Nombre del producto (en el brief)',
      brief_expert: 'Nombre del experto (en el brief)'
    },
    blocking: true
  },
  3: { 
    items: ['landings_generated'],
    labels: { 
      landings_generated: 'Al menos una landing generada'
    },
    blocking: false // Warning pero no bloquea
  },
  4: { 
    items: ['emails_generated'],
    labels: { 
      emails_generated: 'Al menos un email generado'
    },
    blocking: false
  },
  5: { 
    items: ['copy_generated'],
    labels: { 
      copy_generated: 'Al menos un copy de anuncio generado'
    },
    blocking: false
  },
  6: { items: [], labels: {}, blocking: false },
  7: { items: [], labels: {}, blocking: false },
  8: { items: [], labels: {}, blocking: false }
};

const FunnelWizard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Get advisor data from navigation state (if coming from Strategy Advisor)
  const advisorData = location.state?.advisorData;
  
  // LocalStorage key for wizard state - MUST include funnel ID for isolation
  const getStorageKey = useCallback(() => {
    return id ? `launch_ai_wizard_${id}` : 'launch_ai_wizard_draft_new';
  }, [id]);
  
  const [currentStep, _setCurrentStep] = useState(1);
  
  // Ref to track the last step change time to prevent rapid consecutive changes from USER ACTIONS
  const lastUserStepChangeRef = useRef(0);
  const stepChangeDebounceMs = 500; // Minimum 500ms between USER-initiated step changes
  
  // Wrapper to track step changes with optional debounce protection
  // isUserAction: true for button clicks, false for initial load/programmatic changes
  const setCurrentStep = useCallback((value, isUserAction = false) => {
    const now = Date.now();
    
    // Only debounce user-initiated changes, not programmatic/initial changes
    if (isUserAction) {
      const timeSinceLastUserChange = now - lastUserStepChangeRef.current;
      
      if (timeSinceLastUserChange < stepChangeDebounceMs) {
        return; // Ignore rapid consecutive user clicks
      }
      
      lastUserStepChangeRef.current = now;
    }
    
    _setCurrentStep(value);
  }, []);
  const [funnel, setFunnel] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  
  // Auto-save state
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'error' | 'pending'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  
  // Track if initial step from progress has been set (prevent duplicate calls)
  const initialStepSetRef = useRef(false);
  
  // Animation state for step transitions
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('next'); // 'next' | 'prev'
  const [swipeProgress, setSwipeProgress] = useState(0); // -1 to 1 for visual feedback
  
  // Wizard data
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [funnelName, setFunnelName] = useState('');
  const [brief, setBrief] = useState({});
  
  // Assets configuration from strategy (SOURCE OF TRUTH)
  const [assetsConfig, setAssetsConfig] = useState(null);
  
  // Generated assets - PERSISTED
  const [assets, setAssets] = useState({
    landings: {},
    emails: [],
    whatsapp: [],
    adsCopy: [],
    adsImages: [],
  });

  // Step approvals state
  const [stepApprovals, setStepApprovals] = useState({
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: []
  });

  // ========== CRITICAL: Reset all state when funnel ID changes ==========
  // Using a ref to track previous ID and avoid unnecessary resets
  const prevIdRef = useRef(id);
  
  useEffect(() => {
    // Only reset when ID actually changes (not on initial mount with same value)
    if (prevIdRef.current === id && prevIdRef.current !== undefined) return;
    prevIdRef.current = id;
    
    // Reset the initial step tracking ref
    initialStepSetRef.current = false;
    
    // Batch all state resets in a single update cycle
    const resetState = () => {
      setCurrentStep(1);
      setFunnel(null);
      setSelectedStrategy(null);
      setSelectedVariant(null);
      setSelectedClient(null);
      setFunnelName('');
      setBrief({});
      setAssetsConfig(null);
      setAssets({
        landings: {},
        emails: [],
        whatsapp: [],
        adsCopy: [],
        adsImages: [],
      });
      setStepApprovals({
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: [],
        7: []
      });
      
      // If editing existing funnel, it will be loaded by fetchFunnel()
      if (id) {
        setLoading(true);
      }
    };
    
    resetState();
  }, [id]);

  // Landing approvals tracking
  const [landingsApprovalStatus, setLandingsApprovalStatus] = useState({ approved: 0, total: 6 });

  // Navigation blocked message
  const [blockMessage, setBlockMessage] = useState(null);

  // Load from localStorage on mount (only for new funnels, not editing existing)
  useEffect(() => {
    if (!id) {
      const savedDraft = localStorage.getItem(getStorageKey());
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          if (draft.selectedStrategy) setSelectedStrategy(draft.selectedStrategy);
          if (draft.selectedVariant) setSelectedVariant(draft.selectedVariant);
          if (draft.selectedClient) setSelectedClient(draft.selectedClient);
          if (draft.funnelName) setFunnelName(draft.funnelName);
          if (draft.brief) setBrief(draft.brief);
          if (draft.assetsConfig) setAssetsConfig(draft.assetsConfig);
          if (draft.currentStep) setCurrentStep(draft.currentStep);
          if (draft.assets) setAssets(draft.assets);
          if (draft.stepApprovals) setStepApprovals(draft.stepApprovals);
          if (process.env.NODE_ENV === 'development') console.log('Wizard draft loaded from localStorage');
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.error('Error loading wizard draft:', e);
        }
      }
    }
  }, [id, getStorageKey]);

  // ========== Pre-select strategy/variant from URL params (Strategy Advisor) ==========
  useEffect(() => {
    // Only for new funnels, not editing existing ones
    if (id) return;
    
    const strategyParam = searchParams.get('strategy');
    const variantParam = searchParams.get('variant');
    
    if (strategyParam && variantParam) {
      const strategy = getStrategyById(strategyParam);
      const variant = getVariantById(strategyParam, variantParam);
      
      if (strategy && variant) {
        setSelectedStrategy(strategyParam);
        setSelectedVariant(variantParam);
        
        // Pre-fill brief from advisor answers if available
        if (advisorData?.fromAdvisor && advisorData?.answers) {
          const answers = advisorData.answers;
          const preFillBrief = {};
          
          // Map advisor answers to brief fields
          // Price range
          if (answers.price_range) {
            const priceMap = {
              'menos_97': '47€',
              '97_497': '297€',
              '497_1997': '997€',
              'mas_1997': '2.997€',
              'varios': ''
            };
            preFillBrief.price = priceMap[answers.price_range] || '';
          }
          
          // Product type → what_you_sell hint
          if (answers.product_type) {
            const productHints = {
              'infoproducto': 'Curso online / Programa de formación',
              'servicio_alto_valor': 'Servicio de consultoría personalizada',
              'ecommerce': 'Producto físico',
              'saas': 'Software como servicio (SaaS)',
              'local': 'Servicio presencial local'
            };
            preFillBrief.what_you_sell = productHints[answers.product_type] || '';
          }
          
          // Audience size → target_audience hint
          if (answers.audience_size) {
            const audienceHints = {
              'lista_grande': 'Audiencia comprometida con historial de interacción',
              'comunidad_grande': 'Seguidores activos en redes sociales',
              'audiencia_pequena': 'Audiencia inicial en crecimiento',
              'sin_audiencia': 'Tráfico frío, nuevos prospectos',
              'clientes_sin_lista': 'Clientes existentes sin base de datos'
            };
            preFillBrief.audience_type_hint = audienceHints[answers.audience_size] || '';
          }
          
          // Goal → tone_of_voice suggestion
          if (answers.main_goal === 'liquidez') {
            preFillBrief.tone_of_voice = 'directo';
          } else if (answers.main_goal === 'comunidad') {
            preFillBrief.tone_of_voice = 'cercano';
          } else if (answers.main_goal === 'high_ticket') {
            preFillBrief.tone_of_voice = 'profesional';
          }
          
          // Only set if we have data to pre-fill
          if (Object.keys(preFillBrief).length > 0) {
            setBrief(prev => ({ ...prev, ...preFillBrief, from_advisor: true }));
            if (process.env.NODE_ENV === 'development') console.log('[FunnelWizard] Pre-filled brief from Strategy Advisor:', preFillBrief);
          }
        }
        
        toast.success(`Estrategia "${strategy.label}" pre-seleccionada`, {
          description: `Variante: ${variant.label}`,
          duration: 3000
        });
        
        if (process.env.NODE_ENV === 'development') console.log('[FunnelWizard] Pre-selected from Strategy Advisor:', strategyParam, variantParam);
      }
    }
  }, [id, searchParams, advisorData]);

  // ========== Pre-select client from URL param (from Strategy Advisor modal) ==========
  useEffect(() => {
    const clientIdParam = searchParams.get('client_id');
    
    if (clientIdParam && !selectedClient) {
      const loadAndSelectClient = async () => {
        try {
          const response = await api.get('/clients');
          const clientsList = response.data || [];
          const client = clientsList.find(c => c.id === clientIdParam);
          
          if (client) {
            setSelectedClient(client);
            setClients(clientsList);
            if (process.env.NODE_ENV === 'development') console.log('[FunnelWizard] Pre-selected client from URL:', client.name);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') console.error('Error loading client from URL param:', error);
        }
      };
      loadAndSelectClient();
    }
  }, [searchParams, api, selectedClient]);

  // ========== Handle OAuth callback redirects (Meta/Google Ads) ==========
  // When user returns from OAuth, detect params and navigate to Integration step
  useEffect(() => {
    // Only process if we have a funnel ID (existing funnel)
    if (!id) return;
    
    const metaConnected = searchParams.get('meta_connected') === 'true';
    const googleConnected = searchParams.get('google_ads_connected') === 'true';
    const selectAdAccount = searchParams.get('select_ad_account') === 'true';
    const stepParam = searchParams.get('step');
    
    // If coming back from OAuth callback
    if ((metaConnected || googleConnected) && selectAdAccount) {
      // Navigate to Integration step (step 6) to show account selector
      const targetStep = stepParam ? parseInt(stepParam, 10) : 6;
      
      // Store OAuth callback info in sessionStorage so DynamicIntegrationFlow can read it
      // This avoids race conditions with URL param reading
      if (metaConnected) {
        const metaUser = searchParams.get('meta_user') || '';
        sessionStorage.setItem('oauth_callback', JSON.stringify({
          platform: 'meta',
          funnel_id: id,
          meta_user: decodeURIComponent(metaUser),
          timestamp: Date.now()
        }));
      } else if (googleConnected) {
        sessionStorage.setItem('oauth_callback', JSON.stringify({
          platform: 'google_ads',
          funnel_id: id,
          timestamp: Date.now()
        }));
      }
      
      // Clean URL params immediately
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // Only change step if not already there
      if (currentStep !== targetStep) {
        setCurrentStep(targetStep);
        if (process.env.NODE_ENV === 'development') {
          console.log('[FunnelWizard] OAuth callback detected, navigating to step', targetStep);
        }
      }
    }
  }, [id, searchParams, currentStep, setCurrentStep]);

  // Save to localStorage whenever state changes (debounced auto-save)
  useEffect(() => {
    if (!id && (selectedStrategy || selectedVariant || funnelName || Object.keys(brief).length > 0)) {
      const draft = {
        selectedStrategy,
        selectedVariant,
        selectedClient,
        funnelName,
        brief,
        assetsConfig,
        currentStep,
        assets,
        stepApprovals,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(getStorageKey(), JSON.stringify(draft));
    }
  }, [selectedStrategy, selectedVariant, selectedClient, funnelName, brief, assetsConfig, currentStep, assets, stepApprovals, id, getStorageKey]);

  // Clear localStorage when funnel is saved successfully
  const clearDraft = useCallback(() => {
    localStorage.removeItem(getStorageKey());
    // Also clear the generic draft key for new funnels
    localStorage.removeItem('launch_ai_wizard_draft_new');
    if (process.env.NODE_ENV === 'development') console.log('🗑️ Wizard draft cleared');
  }, [getStorageKey]);

  useEffect(() => {
    const loadInitialData = async () => {
      // Fetch clients
      try {
        const response = await api.get('/clients');
        setClients(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Error fetching clients:', error);
      }
      
      // Fetch funnel if editing
      if (id) {
        try {
          const response = await api.get(`/funnels/${id}`);
          const funnelData = response.data;
          setFunnel(funnelData);
          setSelectedStrategy(funnelData.strategy);
          setSelectedVariant(funnelData.strategy_variant);
          setSelectedClient(funnelData.client_id);
          setFunnelName(funnelData.name);
          setBrief(funnelData.brief || {});
          
          // Load assets config - prefer saved custom config, fallback to strategy defaults
          if (funnelData.assets_config) {
            // Use saved custom configuration (has custom order/added/removed pieces)
            setAssetsConfig(funnelData.assets_config);
          } else if (funnelData.strategy && funnelData.strategy_variant) {
            // Fallback to strategy default assets WITH email names from variant
            const variant = getVariantById(funnelData.strategy, funnelData.strategy_variant);
            if (variant?.defaultAssets) {
              const config = { ...variant.defaultAssets };
              // Include email names from variant if they exist
              if (variant.emails && variant.emails.length > 0) {
                config.emailNames = variant.emails.map(e => e.nombre);
                config.originalEmailNames = variant.emails.map(e => e.nombre);
                config.emailsConfig = variant.emails; // Full email config with ids and moments
              }
              setAssetsConfig(config);
            }
          }
          
          // Set initial step based on funnel progress - ONLY ONCE
          if (funnelData.brief && !initialStepSetRef.current) {
            initialStepSetRef.current = true;
            const targetStep = Math.min(funnelData.progress + 1, 7) || 2;
            setCurrentStep(targetStep);
          }
          
          // Load assets from funnel_assets collection
          try {
            const assetsResponse = await api.get(`/funnels/${id}/assets`);
            if (assetsResponse.data?.assets) {
              // Merge funnel.landings into assets if exists (server-persisted landing content)
              const mergedAssets = { ...assetsResponse.data.assets };
              if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
                mergedAssets.landings = funnelData.landings;
              }
              setAssets(mergedAssets);
            } else if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
              // No assets collection but funnel has landings
              setAssets(prev => ({ ...prev, landings: funnelData.landings }));
            }
          } catch (assetsError) {
            if (process.env.NODE_ENV === 'development') console.log('No assets found for funnel, starting fresh');
            // Still try to load landings from funnel data
            if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
              setAssets(prev => ({ ...prev, landings: funnelData.landings }));
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') console.error('Error loading funnel:', error);
          toast.error('Error al cargar el funnel');
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      } else {
        // New funnel - no need to load, just set loading to false
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [id, api, navigate]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching clients:', error);
    }
  };

  // Fetch funnel data (for refresh)
  const fetchFunnel = async () => {
    if (!id) return;
    
    try {
      const response = await api.get(`/funnels/${id}`);
      const funnelData = response.data;
      setFunnel(funnelData);
      
      // Also refresh assets
      try {
        const assetsResponse = await api.get(`/funnels/${id}/assets`);
        if (assetsResponse.data?.assets) {
          // Merge funnel.landings into assets if exists
          const mergedAssets = { ...assetsResponse.data.assets };
          if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
            mergedAssets.landings = funnelData.landings;
          }
          setAssets(mergedAssets);
        } else if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
          setAssets(prev => ({ ...prev, landings: funnelData.landings }));
        }
      } catch (assetsError) {
        if (process.env.NODE_ENV === 'development') console.log('No assets found for funnel');
        if (funnelData.landings && Object.keys(funnelData.landings).length > 0) {
          setAssets(prev => ({ ...prev, landings: funnelData.landings }));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error refreshing funnel:', error);
      toast.error('Error al refrescar el funnel');
    }
  };

  const saveFunnel = useCallback(async (stepCompleted = currentStep, silent = false, landingContentOverride = null) => {
    // Allow saving even with partial data - auto-save should never block
    if (!selectedStrategy || !selectedVariant) {
      return null;
    }

    // Determine landings to save - prefer override, then current assets, then empty
    // NEVER overwrite existing landings with empty unless explicitly set
    let landingsToSave = landingContentOverride || assets.landings || {};
    
    // Ensure landingsToSave is an object (not array)
    if (Array.isArray(landingsToSave)) {
      landingsToSave = {};
    }
    
    // If landingsToSave is empty, don't overwrite - skip the landings field to preserve server data
    const shouldIncludeLandings = typeof landingsToSave === 'object' && Object.keys(landingsToSave).length > 0;

    setSaving(true);
    try {
      const funnelData = {
        client_id: selectedClient,
        name: funnelName || 'Funnel sin nombre',
        strategy: selectedStrategy,
        strategy_variant: selectedVariant,
        brief: brief,
        // Only include landings if we have content to save
        ...(shouldIncludeLandings && { landings: landingsToSave }),
        // Include custom assets config (reordered/added/removed pieces)
        assets_config: assetsConfig || null,
      };

      let response;
      if (funnel?.id) {
        response = await api.put(`/funnels/${funnel.id}`, {
          ...funnelData,
          status: stepCompleted >= 7 ? 'completed' : 'in_progress',
        });
      } else {
        // Only create if we have minimum required data (funnelName is required)
        // client_id is optional - can be null for personal funnels
        if (!funnelName) {
          return null;
        }
        response = await api.post('/funnels', funnelData);
      }
      
      if (response?.data) {
        setFunnel(response.data);
        clearDraft(); // Clear localStorage draft after successful save
        if (!silent) {
          toast.success('Guardado automático completado', { duration: 2000 });
        }
        return response.data;
      }
      return null;
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Error al guardar el funnel';
      if (!silent) {
        toast.error(errorMsg);
      }
      console.error('Auto-save error:', error.response?.data || error);
      return null;
    } finally {
      setSaving(false);
    }
  }, [currentStep, selectedStrategy, selectedVariant, assets.landings, selectedClient, funnelName, brief, assetsConfig, funnel?.id, api, clearDraft]);

  // Format relative time for save indicator
  const formatRelativeTime = useCallback((date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 10) return 'ahora mismo';
    if (seconds < 60) return `hace ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes}m`;
    return `hace ${Math.floor(minutes / 60)}h`;
  }, []);

  // Perform the actual save operation
  const performSave = useCallback(async () => {
    setSaveStatus('saving');
    
    try {
      const result = await saveFunnel(currentStep, true);
      
      if (result) {
        setSaveStatus('saved');
        setLastSavedAt(new Date());
        setHasUnsavedChanges(false);
        retryCountRef.current = 0;
      } else {
        // saveFunnel returned null (missing required data)
        setSaveStatus('saved');
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Auto-save failed:', error);
      setSaveStatus('error');
      
      // Automatic retry with exponential backoff
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const delay = Math.pow(2, retryCountRef.current) * 1000; // 2s, 4s, 8s
        setTimeout(async () => {
          await performSave();
        }, delay);
      }
    }
  }, [currentStep, saveFunnel]);

  // Auto-save function with debounce
  const autoSave = useCallback(async (force = false) => {
    // Don't save if no funnel ID and no minimum data
    if (!funnel?.id && !funnelName) return;
    
    // Cancel previous timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // If forced, save immediately
    if (force) {
      await performSave();
      return;
    }
    
    // 2 second debounce
    setSaveStatus('pending');
    setHasUnsavedChanges(true);
    autoSaveTimerRef.current = setTimeout(async () => {
      await performSave();
    }, 2000);
  }, [funnel?.id, funnelName, performSave]);

  // Trigger auto-save when important data changes (excluding landings which are saved directly)
  useEffect(() => {
    if (!funnel?.id && !funnelName) return;
    
    // Only trigger auto-save if we have a funnel to save to
    if (funnel?.id) {
      autoSave();
    }
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [brief, assetsConfig, selectedStrategy, selectedVariant, funnelName, funnel?.id, autoSave]);

  // Confirmation before closing tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges || saveStatus === 'pending' || saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '¿Seguro que quieres salir? Tienes cambios sin guardar.';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, saveStatus]);

  // Check if step requirements are met
  const checkStepRequirements = useCallback((step) => {
    const requirements = STEP_REQUIREMENTS[step];
    if (!requirements || requirements.items.length === 0) {
      return { complete: true, missing: [], blocking: false };
    }

    const missing = [];

    if (step === 1) {
      if (!selectedStrategy) missing.push('strategy');
      if (!selectedVariant) missing.push('variant');
    }
    
    if (step === 2) {
      if (!selectedClient) missing.push('client');
      if (!funnelName?.trim()) missing.push('funnelName');
      // Brief básico: necesita nombre del producto Y nombre del experto
      const productName = brief?.productName || brief?.product_name;
      const expertName = brief?.expertName || brief?.expert_name;
      const hasBriefBasics = productName?.trim() && expertName?.trim();
      if (!hasBriefBasics) {
        if (!productName?.trim() && !expertName?.trim()) {
          missing.push('brief_product_expert');
        } else if (!productName?.trim()) {
          missing.push('brief_product');
        } else {
          missing.push('brief_expert');
        }
      }
    }
    
    if (step === 3) {
      // Al menos 1 landing generada - check multiple sources
      const landingsGenerated = 
        // Check assets.landings (merged from funnel on load)
        (assets?.landings && Object.keys(assets.landings).length > 0) ||
        // Check funnel.landings directly (server persisted)
        (funnel?.landings && Object.keys(funnel.landings).length > 0) ||
        // Check assetsConfig.landingContent (localStorage draft)
        Object.keys(assetsConfig?.landingContent || {}).length > 0;
      if (!landingsGenerated) missing.push('landings_generated');
    }
    
    if (step === 4) {
      // Al menos 1 email o WhatsApp generado - check multiple sources
      let emailsGenerated = assets?.emails?.length > 0 || assets?.whatsapp?.length > 0;
      
      // Check funnel.sequences from server (emails/whatsapp)
      if (!emailsGenerated && funnel?.sequences) {
        const hasEmails = funnel.sequences.emails && Object.keys(funnel.sequences.emails).some(k => funnel.sequences.emails[k]);
        const hasWhatsapp = funnel.sequences.whatsapp && Object.keys(funnel.sequences.whatsapp).some(k => funnel.sequences.whatsapp[k]);
        emailsGenerated = hasEmails || hasWhatsapp;
      }
      
      // Also check localStorage as SequencesStep stores content there
      if (!emailsGenerated && funnel?.id) {
        try {
          const savedEmails = localStorage.getItem(`sequences_emails_${funnel.id}`);
          const savedWhatsapp = localStorage.getItem(`sequences_whatsapp_${funnel.id}`);
          if (savedEmails) {
            const parsed = JSON.parse(savedEmails);
            emailsGenerated = Object.keys(parsed).filter(k => parsed[k]).length > 0;
          }
          if (!emailsGenerated && savedWhatsapp) {
            const parsed = JSON.parse(savedWhatsapp);
            emailsGenerated = Object.keys(parsed).filter(k => parsed[k]).length > 0;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      if (!emailsGenerated) missing.push('emails_generated');
    }
    
    if (step === 5) {
      // Al menos 1 copy de ad generado - check multiple sources
      let copyGenerated = assets?.adsCopy?.length > 0 ||
        Object.keys(assetsConfig?.copyVariants || {}).length > 0;
      
      // Check funnel.ad_copies from server
      if (!copyGenerated && funnel?.ad_copies) {
        const hasCaptacion = funnel.ad_copies.captacion?.length > 0;
        const hasVentas = funnel.ad_copies.ventas?.length > 0;
        copyGenerated = hasCaptacion || hasVentas;
      }
      
      // Also check localStorage as TrafficStep stores copies there
      if (!copyGenerated && funnel?.id) {
        try {
          const savedCopiesCap = localStorage.getItem(`traffic_copies_cap_${funnel.id}`);
          const savedCopiesVen = localStorage.getItem(`traffic_copies_ven_${funnel.id}`);
          if (savedCopiesCap) {
            const parsed = JSON.parse(savedCopiesCap);
            copyGenerated = parsed.length > 0;
          }
          if (!copyGenerated && savedCopiesVen) {
            const parsed = JSON.parse(savedCopiesVen);
            copyGenerated = parsed.length > 0;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      if (!copyGenerated) missing.push('copy_generated');
    }

    return { 
      complete: missing.length === 0,
      missing,
      labels: requirements.labels,
      blocking: requirements.blocking
    };
  }, [selectedStrategy, selectedVariant, selectedClient, 
      funnelName, brief, assets, assetsConfig, funnel]);

  // Get visual status for stepper
  const getStepStatus = useCallback((step) => {
    if (step < currentStep) {
      // Paso completado — verificar si tiene warnings
      const { complete } = checkStepRequirements(step);
      return complete ? 'completed' : 'completed_with_warnings';
    }
    if (step === currentStep) return 'active';
    return 'pending';
  }, [currentStep, checkStepRequirements]);

  // Handler for landing approval changes
  const handleLandingApprovalChange = useCallback((approvedCount, totalCount) => {
    setLandingsApprovalStatus({ approved: approvedCount, total: totalCount });
  }, []);

  // Handler for assets config updates (reorder, add, remove pieces)
  const handleAssetsConfigUpdate = useCallback((newConfig) => {
    setAssetsConfig(newConfig);
    // Trigger auto-save to persist changes
    if (funnel?.id) {
      autoSave();
    }
  }, [funnel?.id, autoSave]);

  // Can continue to next step (only blocking requirements prevent continuation)
  const canContinue = useCallback(() => {
    const { complete, blocking } = checkStepRequirements(currentStep);
    // Solo bloquea si hay requisitos incompletos Y son bloqueantes
    return complete || !blocking;
  }, [currentStep, checkStepRequirements]);

  // Get missing items for current step
  const getMissingItems = useCallback(() => {
    const { missing, labels, blocking } = checkStepRequirements(currentStep);
    // Solo muestra items faltantes si son bloqueantes
    if (!blocking) return [];
    return missing.map(item => labels[item] || item);
  }, [currentStep, checkStepRequirements]);

  const handleNext = async () => {
    const { complete, missing, labels, blocking } = 
      checkStepRequirements(currentStep);
    
    if (!complete) {
      const missingLabels = missing
        .map(m => labels[m])
        .join('\n• ');
      
      if (blocking) {
        // Bloquea el avance — pasos 1 y 2
        toast.error('Completa estos campos antes de continuar:', {
          description: `• ${missingLabels}`,
          duration: 4000
        });
        return;
      } else {
        // Warning pero permite continuar — pasos 3, 4, 5
        // Track confirmed skips to allow immediate continuation on second click
        const skipKey = `skip_warning_step_${currentStep}`;
        const alreadyConfirmed = sessionStorage.getItem(skipKey) === 'true';
        
        if (alreadyConfirmed) {
          // User already saw warning, allow continuation
          sessionStorage.removeItem(skipKey);
          // Continue below to advance step
        } else {
          // First time - show warning and set confirmation flag
          sessionStorage.setItem(skipKey, 'true');
          toast.warning('Te recomendamos completar esto primero:', {
            description: `• ${missingLabels}\n\nHaz click en "Continuar" de nuevo para avanzar.`,
            duration: 4000
          });
          return; // User must click Continue again
        }
      }
    }

    // Guardado específico según el paso
    if (currentStep === 2) {
      const saved = await saveFunnel(2, true);
      if (!saved) {
        toast.error('Error al guardar. Inténtalo de nuevo.');
        return;
      }
    } else if (funnel?.id) {
      autoSave(true);
    }

    if (currentStep < 8) {
      setCurrentStep(prev => prev + 1, true); // User action
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1 && !isTransitioning) {
      setTransitionDirection('prev');
      setIsTransitioning(true);
      
      // Small delay for animation
      setTimeout(() => {
        setCurrentStep(currentStep - 1, true); // User action
        // Auto-save before going back (preserve all data)
        if (funnel?.id) {
          autoSave(true); // Immediate save when going back
        }
        
        // End transition
        setTimeout(() => setIsTransitioning(false), 300);
      }, 50);
    }
  };

  const goToStep = (step) => {
    if (isTransitioning) return;
    
    // Siempre puede ir hacia atrás
    if (step < currentStep) {
      setTransitionDirection('prev');
      setIsTransitioning(true);
      
      setTimeout(() => {
        if (funnel?.id) autoSave();
        setCurrentStep(step, true); // User action
        setTimeout(() => setIsTransitioning(false), 300);
      }, 50);
      return;
    }
    
    // Para ir hacia adelante valida todos los pasos intermedios
    if (step > currentStep) {
      // Verifica cada paso bloqueante entre el actual y el destino
      for (let s = currentStep; s < step; s++) {
        const { complete, missing, labels, blocking } = 
          checkStepRequirements(s);
        
        if (!complete && blocking) {
          const missingLabels = missing
            .map(m => labels[m])
            .join(', ');
          toast.error(
            `Completa el Paso ${s} primero: ${missingLabels}`
          );
          setCurrentStep(s, true); // User action (redirect to problematic step)
          return;
        }
      }
      
      // Si no hay pasos bloqueantes → permite el salto con animación
      setTransitionDirection('next');
      setIsTransitioning(true);
      
      setTimeout(() => {
        if (funnel?.id) autoSave();
        setCurrentStep(step, true); // User action
        setTimeout(() => setIsTransitioning(false), 300);
      }, 50);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StrategyStep
            key={`strategy-${funnel?.id || 'new'}`}
            selectedStrategy={selectedStrategy}
            selectedVariant={selectedVariant}
            onSelectStrategy={setSelectedStrategy}
            onSelectVariant={setSelectedVariant}
            onAssetsConfigured={setAssetsConfig}
          />
        );
      case 2:
        return (
          <BriefStep
            key={`brief-${funnel?.id || 'new'}`}
            clients={clients}
            selectedClient={selectedClient}
            onSelectClient={setSelectedClient}
            funnelName={funnelName}
            onSetFunnelName={setFunnelName}
            strategy={selectedStrategy}
            variant={selectedVariant}
            brief={brief}
            onUpdateBrief={setBrief}
            funnel={funnel}
            onContinueToLandings={async () => {
              // Save funnel and advance to step 3
              const saved = await saveFunnel(2);
              if (saved) {
                setCurrentStep(3, true); // User action
                toast.success('Brief guardado. Avanzando a Landings...');
              }
            }}
          />
        );
      case 3:
        return (
          <LandingsStep
            key={`landings-${funnel?.id || 'new'}`}
            funnel={funnel}
            brief={brief}
            assets={assets.landings}
            assetsConfig={assetsConfig}
            onAssetsUpdate={(newAssets) => setAssets({ ...assets, landings: newAssets })}
            onAssetsConfigUpdate={handleAssetsConfigUpdate}
            onBriefSync={(updates) => {
              // Bidirectional sync: Update brief when master data changes in landings
              setBrief(prev => ({ ...prev, ...updates }));
            }}
            onAutoSave={(landingContent) => {
              // If landing content is provided, save directly with the content
              if (landingContent && typeof landingContent === 'object' && Object.keys(landingContent).length > 0) {
                // Update assets state
                setAssets(prev => ({ ...prev, landings: landingContent }));
                // Save immediately with the landing content override
                saveFunnel(currentStep, true, landingContent);
              } else {
                autoSave();
              }
            }}
            onApprovalChange={handleLandingApprovalChange}
            onContinueToTraffic={() => {
              // Auto-save and continue to step 4 (Sequences/Emails)
              autoSave();
              setCurrentStep(4, true); // User action
            }}
          />
        );
      case 4:
        return (
          <SequencesStep
            key={`sequences-${funnel?.id || 'new'}`}
            funnel={funnel}
            brief={brief}
            emailAssets={assets.emails}
            whatsappAssets={assets.whatsapp}
            assetsConfig={assetsConfig}
            onEmailsUpdate={(newAssets) => setAssets({ ...assets, emails: newAssets })}
            onWhatsappUpdate={(newAssets) => setAssets({ ...assets, whatsapp: newAssets })}
            onAssetsConfigUpdate={handleAssetsConfigUpdate}
            onEmergencySave={async () => {
              // Emergency save function - creates funnel if it doesn't exist
              return await saveFunnel(currentStep, true);
            }}
          />
        );
      case 5:
        return (
          <TrafficStep
            key={`traffic-${funnel?.id || 'new'}`}
            funnel={funnel}
            brief={brief}
            assetsConfig={assetsConfig}
            copyAssets={assets.adsCopy}
            imageAssets={assets.adsImages}
            onCopyUpdate={(newAssets) => setAssets({ ...assets, adsCopy: newAssets })}
            onImagesUpdate={(newAssets) => setAssets({ ...assets, adsImages: newAssets })}
            onEmergencySave={async () => await saveFunnel(currentStep, true)}
          />
        );
      case 6:
        return (
          <IntegrationStep
            key={`integration-${funnel?.id || 'new'}`}
            funnel={funnel}
            assets={assets}
            brief={brief}
          />
        );
      case 7:
        return (
          <CampaignsStep
            key={`campaigns-${funnel?.id || 'new'}`}
            funnel={funnel}
            brief={brief}
            assets={assets}
            assetsConfig={assetsConfig}
          />
        );
      case 8:
        return (
          <FunnelDashboard
            funnel={funnel}
            assets={assets}
            onRefresh={fetchFunnel}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-brand-500 animate-pulse-glow mx-auto mb-4 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-white" />
            </div>
            <p className="text-text-secondary">Cargando funnel...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Swipe handlers for mobile navigation with animation
  const handleSwipeLeft = () => {
    // Swipe left = next step
    if (currentStep < 7 && canContinue() && !isTransitioning) {
      setTransitionDirection('next');
      handleNext();
    }
  };

  const handleSwipeRight = () => {
    // Swipe right = previous step
    if (currentStep > 1 && !isTransitioning) {
      setTransitionDirection('prev');
      handlePrevious();
    }
  };

  return (
    <MainLayout>
      <div className="space-y-4 lg:space-y-6" data-testid="funnel-wizard">
        {/* Header with Credit Balance and Progress Indicator */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
                {funnel?.name || 'Nuevo Funnel'}
              </h1>
              <p className="text-muted-foreground text-sm sm:text-base mt-1">
                Elige tu estrategia y construye tu funnel completo con IA
              </p>
            </div>
            
            {/* Credit Balance - Always visible */}
            <CreditBalance className="self-start sm:self-center" showRefresh />
          </div>
          
          {/* Compact Progress Indicator - Mobile friendly */}
          <div className="sm:hidden">
            <StepProgressIndicator 
              currentStep={currentStep} 
              totalSteps={8}
              stepName={WIZARD_STEPS[currentStep - 1]?.name}
            />
          </div>
        </div>

        {/* Progress Bar - 8 Steps - Desktop: Full bar, Mobile: Hidden (using compact indicator above) */}
        <div className="relative overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 hidden sm:block">
          <div className="flex items-center justify-between min-w-[600px] sm:min-w-0">
            {WIZARD_STEPS.map((step, index) => {
              const stepStatus = getStepStatus(step.id);
              const isAccessible = step.id <= currentStep || funnel?.id;

              // Determine colors based on status
              const getStepColors = () => {
                switch (stepStatus) {
                  case 'completed':
                    return 'bg-emerald-500 text-white';
                  case 'completed_with_warnings':
                    return 'bg-amber-500 text-white';
                  case 'active':
                    return 'bg-brand-500 text-white shadow-glow-brand';
                  default:
                    return 'bg-card border border-border text-muted-foreground';
                }
              };

              const getLineColor = () => {
                if (step.id < currentStep) {
                  return stepStatus === 'completed' ? 'bg-emerald-500' : 'bg-amber-500';
                }
                return step.id <= currentStep ? 'bg-brand-500' : 'bg-border';
              };

              return (
                <div key={step.id} className="flex flex-col items-center flex-1">
                  <div className="flex items-center w-full">
                    {index > 0 && (
                      <div 
                        className={`h-0.5 flex-1 transition-colors duration-300 ${getLineColor()}`}
                      />
                    )}
                    
                    <button
                      onClick={() => isAccessible && goToStep(step.id)}
                      disabled={!isAccessible}
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-all shrink-0 ${getStepColors()} ${isAccessible ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'}`}
                      data-testid={`progress-step-${step.id}`}
                    >
                      {stepStatus === 'completed' || stepStatus === 'completed_with_warnings' 
                        ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> 
                        : step.id}
                    </button>
                    
                    {index < WIZARD_STEPS.length - 1 && (
                      <div 
                        className={`h-0.5 flex-1 transition-colors duration-300 ${
                          step.id < currentStep ? getLineColor() : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                  
                  <span className={`text-[10px] sm:text-xs mt-1 sm:mt-2 text-center transition-colors whitespace-nowrap ${
                    stepStatus === 'active' ? 'text-brand-500 font-medium' 
                    : stepStatus === 'completed_with_warnings' ? 'text-amber-500'
                    : 'text-muted-foreground'
                  }`}>
                    {step.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content - with swipe gestures on mobile */}
        <div 
          className={`
            bg-card border border-border rounded-xl lg:rounded-2xl min-h-[400px] lg:min-h-[500px] mb-8
            relative shadow-glass-light touch-pan-y flex flex-col
            transition-all duration-300 ease-out
            ${isTransitioning ? 'pointer-events-none' : ''}
          `}
          onTouchStart={(e) => {
            if (isTransitioning) return;
            // Only track horizontal swipes on the main container
            const touch = e.touches[0];
            e.currentTarget.dataset.touchStartX = touch.clientX;
            e.currentTarget.dataset.touchStartY = touch.clientY;
            e.currentTarget.dataset.touchStartTime = Date.now();
          }}
          onTouchMove={(e) => {
            if (isTransitioning) return;
            // Detect if it's a horizontal swipe and update visual feedback
            const touch = e.touches[0];
            const startX = parseFloat(e.currentTarget.dataset.touchStartX);
            const startY = parseFloat(e.currentTarget.dataset.touchStartY);
            const deltaX = touch.clientX - startX;
            const deltaY = touch.clientY - startY;
            
            // If more horizontal than vertical, it might be a swipe
            if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
              e.currentTarget.dataset.isSwipe = 'true';
              e.currentTarget.dataset.swipeDirection = deltaX > 0 ? 'right' : 'left';
              
              // Update swipe progress for visual feedback (max 50px offset)
              const progress = Math.max(-50, Math.min(50, deltaX * 0.3));
              setSwipeProgress(progress);
            }
          }}
          onTouchEnd={(e) => {
            const isSwipeGesture = e.currentTarget.dataset.isSwipe === 'true';
            const direction = e.currentTarget.dataset.swipeDirection;
            const startTime = parseFloat(e.currentTarget.dataset.touchStartTime);
            const duration = Date.now() - startTime;
            const startX = parseFloat(e.currentTarget.dataset.touchStartX);
            
            // Get the final position (we need to get it from the last known position)
            const finalDeltaX = Math.abs(swipeProgress) * 3.33; // Reverse the 0.3 multiplier
            
            // Check if it's a valid swipe (either distance > 50 or velocity is high)
            const isValidSwipe = isSwipeGesture && (finalDeltaX > 50 || (finalDeltaX > 30 && duration < 300));
            
            if (isValidSwipe) {
              if (direction === 'left') {
                handleSwipeLeft();
              } else if (direction === 'right') {
                handleSwipeRight();
              }
            }
            
            // Reset visual feedback
            setSwipeProgress(0);
            
            // Reset data attributes
            delete e.currentTarget.dataset.touchStartX;
            delete e.currentTarget.dataset.touchStartY;
            delete e.currentTarget.dataset.touchStartTime;
            delete e.currentTarget.dataset.isSwipe;
            delete e.currentTarget.dataset.swipeDirection;
          }}
        >
          {/* Swipe Indicator - Left */}
          <div 
            className={`
              absolute left-0 top-0 bottom-0 w-16 
              bg-gradient-to-r from-brand-500/20 to-transparent
              flex items-center justify-start pl-2
              transition-opacity duration-150
              pointer-events-none z-10
              ${swipeProgress > 20 ? 'opacity-100' : 'opacity-0'}
            `}
          >
            <div className="w-8 h-8 rounded-full bg-brand-500/30 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-brand-500 rotate-180" />
            </div>
          </div>
          
          {/* Swipe Indicator - Right */}
          <div 
            className={`
              absolute right-0 top-0 bottom-0 w-16 
              bg-gradient-to-l from-brand-500/20 to-transparent
              flex items-center justify-end pr-2
              transition-opacity duration-150
              pointer-events-none z-10
              ${swipeProgress < -20 ? 'opacity-100' : 'opacity-0'}
            `}
          >
            <div className="w-8 h-8 rounded-full bg-brand-500/30 flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-brand-500" />
            </div>
          </div>
          
          {/* Content with slide animation */}
          <div 
            className={`
              p-4 sm:p-6 lg:p-8 flex-1 overflow-y-auto
              transition-all duration-300 ease-out
              ${isTransitioning 
                ? transitionDirection === 'next'
                  ? 'opacity-0 translate-x-[-20px]'
                  : 'opacity-0 translate-x-[20px]'
                : 'opacity-100 translate-x-0'
              }
            `}
            style={{
              transform: `translateX(${swipeProgress}px)`,
            }}
          >
            {renderStep()}
          </div>
          
          {/* Footer with navigation buttons */}
          {currentStep < 8 && (
            <div className="sticky bottom-0 p-3 sm:p-4 lg:p-6 border-t border-border bg-card/95 backdrop-blur-sm rounded-b-xl lg:rounded-b-2xl flex-shrink-0">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
                <Button
                  variant="ghost"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="text-text-secondary hover:text-text-main w-full sm:w-auto order-2 sm:order-1"
                >
                  ← Anterior
                </Button>
                
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto order-1 sm:order-2">
                  {/* Save Status Indicator - Hidden on very small screens */}
                  <div className="hidden xs:flex items-center gap-2 text-xs">
                    {saveStatus === 'saving' && (
                      <div className="flex items-center gap-1.5 text-text-muted animate-pulse">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-ping" />
                        <span>Guardando...</span>
                      </div>
                    )}
                    
                    {saveStatus === 'pending' && (
                      <div className="flex items-center gap-1.5 text-text-muted">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        <span className="hidden sm:inline">Cambios sin guardar</span>
                      </div>
                    )}
                    
                    {saveStatus === 'saved' && lastSavedAt && (
                      <div className="flex items-center gap-1.5 text-[#00D68F]">
                        <Check className="w-3 h-3" />
                        <span className="hidden sm:inline">Guardado {formatRelativeTime(lastSavedAt)}</span>
                      </div>
                    )}
                    
                    {saveStatus === 'error' && (
                      <div className="flex items-center gap-1.5 text-red-400">
                        <AlertCircle className="w-3 h-3" />
                        <span 
                          className="cursor-pointer hover:underline"
                          onClick={() => performSave()}
                        >
                          Error · Reintentar
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Show missing items hint - Compact on mobile */}
                  {!canContinue() && (
                    <div className="flex items-center gap-2 text-amber-400 text-xs">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate max-w-[150px] sm:max-w-none">Falta: {getMissingItems().join(', ')}</span>
                    </div>
                  )}
                  
                  <Button
                    onClick={handleNext}
                    disabled={saving || !canContinue()}
                    className={`w-full sm:w-auto px-6 sm:px-8 transition-all ${
                      canContinue() 
                        ? 'bg-brand-500 hover:bg-brand-hover text-white shadow-glow-brand' 
                        : 'bg-surface border border-border-subtle text-text-muted cursor-not-allowed'
                    }`}
                    data-testid="next-step-btn"
                  >
                    {!canContinue() && <Lock className="w-4 h-4 mr-2" />}
                    {saving ? 'Guardando...' : currentStep === 7 ? 'Ver Dashboard' : 'Continuar'}
                    {canContinue() && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FunnelWizard;
